import { gsap } from "gsap";
import { Flip, ScrambleTextPlugin, ScrollTrigger, SplitText } from "gsap/all";
import { InertiaPlugin } from "gsap/InertiaPlugin";
gsap.registerPlugin(
  InertiaPlugin,
  SplitText,
  ScrambleTextPlugin,
  ScrollTrigger,
  Flip,
);

export function initMomentumBasedHover() {
  const isMobile = !window.matchMedia("(hover: hover) and (pointer: fine)")
    .matches;

  if (isMobile) {
    initMobileMarquee();
  } else {
    initDesktopHover();
  }
}

// ========== MOBILE: Infinite Marquee with Curve ==========
function initMobileMarquee() {
  const roots = document.querySelectorAll("[data-momentum-hover-init]");

  roots.forEach((root) => {
    const container = root.querySelector(".block-hero-home__images");
    const items = Array.from(
      container.querySelectorAll("[data-momentum-hover-element]"),
    );

    if (!container || items.length < 2) return;

    const speed = 0.5;

    // Config curva
    const curve = {
      maxY: 10, // Píxeles de desplazamiento vertical máximo
      maxRotate: 8, // Grados de rotación máxima
    };

    setTimeout(() => {
      // Limpiar estilos
      items.forEach((item) => {
        gsap.set(item, { clearProps: "all" });
        const target = item.querySelector("[data-momentum-hover-target]");
        if (target) gsap.set(target, { clearProps: "all" });
      });

      // Duplicar
      items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.removeAttribute("style");
        const cloneTarget = clone.querySelector("[data-momentum-hover-target]");
        if (cloneTarget) cloneTarget.removeAttribute("style");
        container.appendChild(clone);
      });

      container.classList.add("marquee-ready");

      // Todos los items (originales + clones)
      const allItems = container.querySelectorAll(
        "[data-momentum-hover-element]",
      );

      let position = 0;
      let animationId = null;

      const gap = 46;
      let totalOriginalWidth = 0;
      items.forEach((item) => {
        totalOriginalWidth += item.offsetWidth + gap;
      });

      const screenCenter = window.innerWidth / 2;

      function animate() {
        position -= speed;

        if (Math.abs(position) >= totalOriginalWidth) {
          position += totalOriginalWidth;
        }

        container.style.transform = `translateX(${position}px)`;

        // Aplicar curva a cada item
        allItems.forEach((item) => {
          const rect = item.getBoundingClientRect();
          const itemCenter = rect.left + rect.width / 2;

          // Distancia del centro (normalizada de -1 a 1)
          const distanceFromCenter = (itemCenter - screenCenter) / screenCenter;

          // Curva parabólica: centro arriba, lados abajo
          const yOffset = Math.abs(distanceFromCenter) * curve.maxY;

          // Rotación: izquierda rota +, derecha rota -
          const rotation = distanceFromCenter * curve.maxRotate;

          item.style.transform = `translateY(${yOffset}px) rotate(${rotation}deg)`;
        });

        animationId = requestAnimationFrame(animate);
      }

      animate();

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!animationId) animate();
          } else {
            if (animationId) {
              cancelAnimationFrame(animationId);
              animationId = null;
            }
          }
        });
      });

      observer.observe(root);
    }, 1000);
  });
}

// ========== DESKTOP: Original Hover Effect ==========
function initDesktopHover() {
  const xyMultiplier = 30;
  const rotationMultiplier = 20;
  const inertiaResistance = 200;

  const clampXY = gsap.utils.clamp(-1080, 1080);
  const clampRot = gsap.utils.clamp(-60, 60);

  document.querySelectorAll("[data-momentum-hover-init]").forEach((root) => {
    let prevX = 0,
      prevY = 0;
    let velX = 0,
      velY = 0;
    let rafId = null;

    root.addEventListener("mousemove", (e) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        velX = e.clientX - prevX;
        velY = e.clientY - prevY;
        prevX = e.clientX;
        prevY = e.clientY;
        rafId = null;
      });
    });

    root.querySelectorAll("[data-momentum-hover-element]").forEach((el) => {
      el.addEventListener("mouseenter", (e) => {
        const target = el.querySelector("[data-momentum-hover-target]");
        if (!target) return;

        const { left, top, width, height } = target.getBoundingClientRect();
        const centerX = left + width / 2;
        const centerY = top + height / 2;
        const offsetX = e.clientX - centerX;
        const offsetY = e.clientY - centerY;

        const rawTorque = offsetX * velY - offsetY * velX;
        const leverDist = Math.hypot(offsetX, offsetY) || 1;
        const angularForce = rawTorque / leverDist;

        const velocityX = clampXY(velX * xyMultiplier);
        const velocityY = clampXY(velY * xyMultiplier);
        const rotationVelocity = clampRot(angularForce * rotationMultiplier);

        gsap.to(target, {
          inertia: {
            x: { velocity: velocityX, end: 0 },
            y: { velocity: velocityY, end: 0 },
            rotation: { velocity: rotationVelocity, end: 0 },
            resistance: inertiaResistance,
          },
        });
      });
    });
  });
}

export function initCenteredScalingNavigationBar(scope = document) {
  // Si tu nav vive fuera del container (header global), NO uses scope aquí:
  const root = document;

  // ✅ evita doble init en navegaciones Barba
  if (root.documentElement.dataset.navInit === "true") return;
  root.documentElement.dataset.navInit = "true";

  const navigationInnerItems = root.querySelectorAll("[data-navigation-item]");
  navigationInnerItems.forEach((item, index) => {
    item.style.transitionDelay = `${index * 0.05}s`;
  });

  const getNavStatusEl = () => root.querySelector("[data-navigation-status]");

  const toggles = Array.from(
    root.querySelectorAll('[data-navigation-toggle="toggle"]'),
  );
  const closes = Array.from(
    root.querySelectorAll('[data-navigation-toggle="close"]'),
  );

  const onToggle = () => {
    const navStatusEl = getNavStatusEl();
    if (!navStatusEl) return;

    const active =
      navStatusEl.getAttribute("data-navigation-status") === "active";
    navStatusEl.setAttribute(
      "data-navigation-status",
      active ? "not-active" : "active",
    );
  };

  const onClose = () => {
    const navStatusEl = getNavStatusEl();
    if (!navStatusEl) return;
    navStatusEl.setAttribute("data-navigation-status", "not-active");
  };

  toggles.forEach((btn) => btn.addEventListener("click", onToggle));
  closes.forEach((btn) => btn.addEventListener("click", onClose));

  // ✅ Cerrar al clicar un link
  navigationInnerItems.forEach((link) =>
    link.addEventListener("click", () => {
      onClose();
    }),
  );

  // (Opcional) cleanup por si alguna vez quieres re-init
  return () => {
    root.documentElement.dataset.navInit = "false";
    toggles.forEach((btn) => btn.removeEventListener("click", onToggle));
    closes.forEach((btn) => btn.removeEventListener("click", onClose));
  };
}

export function initHighlightText() {
  let splitHeadingTargets = document.querySelectorAll("[data-highlight-text]");
  splitHeadingTargets.forEach((heading) => {
    const scrollStart =
      heading.getAttribute("data-highlight-scroll-start") || "top 90%";
    const scrollEnd =
      heading.getAttribute("data-highlight-scroll-end") || "center 40%";
    const fadedValue = heading.getAttribute("data-highlight-fade") || 0.2; // Opacity of letter
    const staggerValue = heading.getAttribute("data-highlight-stagger") || 0.1; // Smoother reveal

    new SplitText(heading, {
      type: "words, chars",
      autoSplit: true,
      onSplit(self) {
        let ctx = gsap.context(() => {
          let tl = gsap.timeline({
            scrollTrigger: {
              scrub: true,
              trigger: heading,
              start: scrollStart,
              end: scrollEnd,
            },
          });
          tl.from(self.chars, {
            autoAlpha: fadedValue,
            stagger: staggerValue,
            ease: "linear",
          });
        });
        return ctx; // return our animations so GSAP can clean them up when onSplit fires
      },
    });
  });
}

function updateCutX(el) {
  const inner = el.querySelector(".split-hover__inner");
  const left = el.querySelector(".split-hover__left");
  if (!inner || !left) return;

  const elRect = el.getBoundingClientRect();
  const leftRect = left.getBoundingClientRect();

  // X del corte en coordenadas de el:
  // right del bloque izquierdo - left del propio elemento
  let cutX = leftRect.right - elRect.left;

  // opcional: si quieres que caiga en el centro del "hueco"
  const gap = parseFloat(getComputedStyle(el).getPropertyValue("--gap")) || 0;
  cutX += gap / 2;

  el.style.setProperty("--cut-x", `${cutX}px`);
}

export function initSplitHover(scope = document) {
  // No aplicar en móvil
  const isMobile = window.matchMedia("(max-width: 767px)").matches;

  scope.querySelectorAll("[data-split]").forEach((el) => {
    if (el.dataset.splitReady) return;
    el.dataset.splitReady = "true";

    // En móvil: solo dejamos el texto tal cual
    if (isMobile) return;

    // rotación personalizada o random suave
    let rot = el.dataset.rot;

    if (rot === undefined || rot === "") {
      rot = (Math.random() * 16 - 10).toFixed(1);
    }

    el.style.setProperty("--rot", `${rot}deg`);

    const text = el.textContent.trim();
    const words = text.split(/\s+/);
    if (words.length < 2) return;

    const mid = Math.ceil(words.length / 2);
    const leftText = words.slice(0, mid).join(" ");
    const rightText = words.slice(mid).join(" ");

    const img = el.dataset.img;
    if (img) el.style.setProperty("--img", `url("${img}")`);

    el.innerHTML = `
      <span class="split-hover__inner">
        <span class="split-hover__left">${leftText}</span>
        <span class="split-hover__right">${rightText}</span>
      </span>
    `;

    requestAnimationFrame(() => updateCutX(el));

    const ro = new ResizeObserver(() => updateCutX(el));
    ro.observe(el);
    el._splitRO = ro;

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => updateCutX(el));
    }
  });
}

export function initParallax(scope = document) {
  const items = Array.from(scope.querySelectorAll("[data-parallax]"));
  if (!items.length) return;

  let ticking = false;

  const update = () => {
    const vh = window.innerHeight;
    const scrollY = window.scrollY;

    items.forEach((el) => {
      const speed = parseFloat(el.dataset.parallaxSpeed) || 0.1;

      const rect = el.getBoundingClientRect();
      const elTop = rect.top + scrollY;
      const elCenter = elTop + rect.height / 2;

      // distancia del centro del viewport
      const delta = scrollY + vh / 2 - elCenter;

      const translateY = delta * speed;

      el.style.setProperty("--parallax-y", `${translateY.toFixed(2)}px`);
    });

    ticking = false;
  };

  const onScroll = () => {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  };

  update();
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", update);

  // cleanup por si usas Barba
  return () => {
    window.removeEventListener("scroll", onScroll);
    window.removeEventListener("resize", update);
  };
}

export function initStickyFeatures(root) {
  const wraps = Array.from(
    (root || document).querySelectorAll("[data-sticky-feature-wrap]"),
  );
  if (!wraps.length) return;

  wraps.forEach((w) => {
    const visualWraps = Array.from(
      w.querySelectorAll("[data-sticky-feature-visual-wrap]"),
    );
    const items = Array.from(w.querySelectorAll("[data-sticky-feature-item]"));
    const progressBar = w.querySelector("[data-sticky-feature-progress]");

    if (visualWraps.length !== items.length) {
      console.warn(
        "[initStickyFeatures] visualWraps and items count do not match:",
        {
          visualWraps: visualWraps.length,
          items: items.length,
          wrap: w,
        },
      );
    }

    const count = Math.min(visualWraps.length, items.length);
    if (count < 1) return;

    const rm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const DURATION = rm ? 0.01 : 0.75; // If user prefers reduced motion, reduce duration
    const EASE = "power4.inOut";
    const SCROLL_AMOUNT = 0.9; // % of scroll used for step transitions

    const getTexts = (el) =>
      Array.from(el.querySelectorAll("[data-sticky-feature-text]"));

    if (visualWraps[0])
      gsap.set(visualWraps[0], { clipPath: "inset(0% round 0.75em)" });
    gsap.set(items[0], { autoAlpha: 1 });

    let currentIndex = 0;

    // Transition Function
    function transition(fromIndex, toIndex) {
      if (fromIndex === toIndex) return;
      const tl = gsap.timeline({ defaults: { overwrite: "auto" } });

      if (fromIndex < toIndex) {
        tl.to(
          visualWraps[toIndex],
          {
            clipPath: "inset(0% round 0.75em)",
            duration: DURATION,
            ease: EASE,
          },
          0,
        );
      } else {
        tl.to(
          visualWraps[fromIndex],
          {
            clipPath: "inset(50% round 0.75em)",
            duration: DURATION,
            ease: EASE,
          },
          0,
        );
      }
      animateOut(items[fromIndex]);
      animateIn(items[toIndex]);
    }

    // Fade out text content items
    function animateOut(itemEl) {
      const texts = getTexts(itemEl);
      gsap.to(texts, {
        autoAlpha: 0,
        y: -30,
        ease: "power4.out",
        duration: 0.4,
        onComplete: () => gsap.set(itemEl, { autoAlpha: 0 }),
      });
    }

    // Reveal incoming text content items
    function animateIn(itemEl) {
      const texts = getTexts(itemEl);
      gsap.set(itemEl, { autoAlpha: 1 });
      gsap.fromTo(
        texts,
        {
          autoAlpha: 0,
          y: 30,
        },
        {
          autoAlpha: 1,
          y: 0,
          ease: "power4.out",
          duration: DURATION,
          stagger: 0.1,
        },
      );
    }

    const steps = Math.max(1, count - 1);

    ScrollTrigger.create({
      trigger: w,
      start: "center center",
      end: () => `+=${steps * 100}%`,
      pin: true,
      scrub: true,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        const p = Math.min(self.progress, SCROLL_AMOUNT) / SCROLL_AMOUNT;
        let idx = Math.floor(p * steps + 1e-6);
        idx = Math.max(0, Math.min(steps, idx));

        gsap.to(progressBar, {
          scaleX: p,
          ease: "none",
        });

        if (idx !== currentIndex) {
          transition(currentIndex, idx);
          currentIndex = idx;
        }
      },
    });
  });
}

export function initFooterParallax() {
  // 1) Matar TODOS los ScrollTriggers que tengan id de footer
  ScrollTrigger.getAll().forEach((st) => {
    if (st.vars?.id?.startsWith("footer-parallax")) {
      st.kill();
    }
  });

  const elements = document.querySelectorAll("[data-footer-parallax]");

  if (!elements.length) {
    return;
  }

  elements.forEach((el, index) => {
    const inner = el.querySelector("[data-footer-parallax-inner]");
    const dark = el.querySelector("[data-footer-parallax-dark]");

    if (!inner && !dark) {
      return;
    }

    if (inner) gsap.killTweensOf(inner);
    if (dark) gsap.killTweensOf(dark);

    // Inner empieza abajo (yPercent: 20) y sube hasta su posición natural (0)
    // Dark empieza visible (opacity: 0.5) y desaparece (0) → da sensación de desvelar
    if (inner) gsap.set(inner, { yPercent: 20 });
    if (dark) gsap.set(dark, { opacity: 0.5 });

    const tl = gsap.timeline({
      scrollTrigger: {
        id: `footer-parallax-${index}`,
        trigger: el,
        start: "top 90%",
        end: "top 15%",
        scrub: 0.6,
        invalidateOnRefresh: true,
      },
    });

    if (inner) {
      tl.to(inner, {
        yPercent: 0,
        ease: "none",
      });
    }

    if (dark) {
      tl.to(
        dark,
        {
          opacity: 0,
          ease: "none",
        },
        "<",
      );
    }
  });

  // Refresh después de crear
  ScrollTrigger.refresh();
}

export function initDescriptionGallery() {
  const wrapper = document.querySelector("[data-gallery='wrapper']");
  const inner = document.querySelector("[data-gallery='inner']");
  const items = document.querySelectorAll("[data-gallery='item']");

  if (!wrapper || !inner || items.length === 0) return;

  // Timeline principal
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: wrapper,
      start: "top 60%",
      end: "top 20%",
      scrub: true,
      // markers: true,
    },
  });

  // 1. Animación de crecimiento del wrapper
  tl.fromTo(wrapper, { width: "40vw" }, { width: "100%", ease: "none" });

  // 2. Scroll horizontal (se activa después de que termine la primera animación)
  // Calculamos el ancho total del scroll
  const getScrollAmount = () => {
    return -(inner.scrollWidth - window.innerWidth);
  };

  // Esperamos a que el DOM esté listo para calcular dimensiones
  ScrollTrigger.create({
    trigger: wrapper,
    start: "top top",
    end: () => `+=${inner.scrollWidth - window.innerWidth}`,
    pin: true,
    scrub: 1,
    anticipatePin: 1,
    invalidateOnRefresh: true,
    animation: gsap.to(inner, {
      x: getScrollAmount,
      ease: "none",
    }),
    // markers: true,
  });
}

export function initPinnedInstalaciones(scope = document) {
  const section = scope.querySelector("[data-pin-gallery]");
  if (!section) return;

  const pinEl = section.querySelector(".section-instalaciones__pin");
  const rail = section.querySelector("[data-rail]");
  if (!pinEl || !rail) return;

  const isMobile = !window.matchMedia("(hover: hover) and (pointer: fine)")
    .matches;

  // Limpiar triggers anteriores
  ScrollTrigger.getAll().forEach((st) => {
    if (st.trigger === pinEl) st.kill();
  });

  const getMaxShift = () => {
    const imgs = Array.from(
      rail.querySelectorAll(".section-instalaciones__img"),
    );
    if (!imgs.length) return 0;

    const bottoms = imgs.map((el) => el.offsetTop + el.offsetHeight);
    const contentH = Math.max(...bottoms);
    return Math.max(0, contentH - window.innerHeight);
  };

  const build = () => {
    const maxShift = getMaxShift();
    gsap.set(rail, { y: 0 });

    if (maxShift <= 0) return;

    if (isMobile) {
      // MOBILE: Usar CSS sticky, solo animar el rail
      pinEl.classList.add("use-sticky");

      gsap.to(rail, {
        y: -maxShift,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top top",
          end: () => `+=${maxShift + window.innerHeight * 0.6}`,
          scrub: 0.5,
          invalidateOnRefresh: true,
        },
      });
    } else {
      // DESKTOP: Pin normal con GSAP
      gsap.to(rail, {
        y: -maxShift,
        ease: "none",
        scrollTrigger: {
          trigger: pinEl,
          start: "top top",
          end: () => `+=${maxShift + window.innerHeight * 0.6}`,
          scrub: true,
          pin: true,
          pinSpacing: true,
          invalidateOnRefresh: true,
        },
      });
    }
  };

  build();
  ScrollTrigger.addEventListener("refreshInit", () => gsap.set(rail, { y: 0 }));
  window.addEventListener("resize", () => ScrollTrigger.refresh());
}

export function initDirectionalListHover() {
  const directionMap = {
    top: "translateY(-101%)",
    bottom: "translateY(101%)",
    left: "translateX(-101%)",
    right: "translateX(101%)",
  };

  document.querySelectorAll("[data-directional-hover]").forEach((container) => {
    const type = container.getAttribute("data-type") || "all";

    container
      .querySelectorAll("[data-directional-hover-item]")
      .forEach((item) => {
        const tile = item.querySelector("[data-directional-hover-tile]");
        if (!tile) return;

        const descWrap = item.querySelector("[data-expand-wrap]");

        // Pre-calcular la altura natural del contenido expandible
        if (descWrap) {
          gsap.set(descWrap, { height: 0, overflow: "hidden" });
        }

        item.addEventListener("mouseenter", (e) => {
          const dir = getDirection(e, item, type);
          tile.style.transition = "none";
          tile.style.transform = directionMap[dir] || "translate(0, 0)";
          void tile.offsetHeight;
          tile.style.transition = "";
          tile.style.transform = "translate(0%, 0%)";
          item.setAttribute("data-status", `enter-${dir}`);

          if (descWrap) {
            gsap.to(descWrap, {
              height: "auto",
              duration: 0.5,
              ease: "power3.out",
              overwrite: true,
            });
          }
        });

        item.addEventListener("mouseleave", (e) => {
          const dir = getDirection(e, item, type);
          item.setAttribute("data-status", `leave-${dir}`);
          tile.style.transform = directionMap[dir] || "translate(0, 0)";

          if (descWrap) {
            gsap.to(descWrap, {
              height: 0,
              duration: 0.4,
              ease: "power3.in",
              overwrite: true,
            });
          }
        });
      });

    function getDirection(event, el, type) {
      const { left, top, width: w, height: h } = el.getBoundingClientRect();
      const x = event.clientX - left;
      const y = event.clientY - top;

      if (type === "y") return y < h / 2 ? "top" : "bottom";
      if (type === "x") return x < w / 2 ? "left" : "right";

      const distances = {
        top: y,
        right: w - x,
        bottom: h - y,
        left: x,
      };

      return Object.entries(distances).reduce((a, b) =>
        a[1] < b[1] ? a : b,
      )[0];
    }
  });
}

export function initBlockClubsCollaboration() {
  const triggers = document.querySelectorAll("[data-collab-trigger]");

  triggers.forEach((row) => {
    const card = row.querySelector(".collab-card");
    if (!card) return;

    // Mover la card al body para que el z-index y el fixed funcionen bien
    document.body.appendChild(card);

    row.addEventListener("mouseenter", (e) => {
      document
        .querySelectorAll(".collab-card")
        .forEach((c) => c.classList.remove("is-visible"));

      document.body.style.cursor = "none";
      row.style.cursor = "none";

      card.classList.add("is-visible");
      card.offsetHeight; // reflow

      requestAnimationFrame(() => updateCardPos(e, card));
    });

    row.addEventListener("mousemove", (e) => {
      if (card.classList.contains("is-visible")) {
        updateCardPos(e, card);
      }
    });

    row.addEventListener("mouseleave", () => {
      card.classList.remove("is-visible");
      document.body.style.cursor = "";
      row.style.cursor = "";
    });
  });

  function updateCardPos(e, card) {
    const cardW = card.offsetWidth || 380;
    const cardH = card.offsetHeight || 200;
    const offset = 20;

    let x = e.clientX + offset;
    let y = e.clientY - cardH / 2;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Si la card se sale por la derecha, la ponemos a la izquierda del cursor
    if (x + cardW + offset > vw) x = e.clientX - cardW - offset;

    y = Math.max(10, Math.min(y, vh - cardH - 10));

    card.style.left = `${x}px`;
    card.style.top = `${y}px`;
  }
}

export function initBlockClubsTraining() {
  document.querySelectorAll("[data-training-block]").forEach((block) => {
    const desc   = block.querySelector("[data-training-desc]");
    const label  = block.querySelector("[data-training-label]");
    const topics = Array.from(block.querySelectorAll("[data-training-topic]"));
    if (!topics.length && !desc) return;

    const els = [desc, label, ...topics].filter(Boolean);
    gsap.set(els, { opacity: 0, y: 20 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: block,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    if (desc)  tl.to(desc,  { opacity: 0.75, y: 0, duration: 0.5, ease: "power3.out" });
    if (label) tl.to(label, { opacity: 0.4,  y: 0, duration: 0.35, ease: "power3.out" }, desc ? "-=0.2" : 0);

    tl.to(topics, {
      opacity: 1,
      y: 0,
      duration: 0.4,
      stagger: 0.07,
      ease: "power3.out",
    }, label ? "-=0.1" : (desc ? "-=0.1" : 0));
  });
}

export function initBlockClubsProcess() {
  document.querySelectorAll("[data-process-step]").forEach((step) => {
    const num       = step.querySelector("[data-process-num]");
    const title     = step.querySelector("[data-process-title]");
    const desc      = step.querySelector("[data-process-desc]");
    const list      = step.querySelector("[data-process-list]");
    const listItems = list ? Array.from(list.querySelectorAll("li")) : [];

    // Estado inicial — todos ocultos
    if (num)              gsap.set(num,       { opacity: 0, y: 20 });
    if (title)            gsap.set(title,     { opacity: 0, y: 20 });
    if (desc)             gsap.set(desc,      { opacity: 0, y: 20 });
    if (listItems.length) gsap.set(listItems, { opacity: 0, y: 20 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: step,
        start: "top 80%",
        toggleActions: "play none none none",
      },
    });

    // 1. Número — opacidad final 0.2 (dimmed, decorativo)
    if (num) {
      tl.to(num, { opacity: 0.2, y: 0, duration: 0.4, ease: "power3.out" });
    }

    // 2. Título
    if (title) {
      tl.to(title, { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" }, "-=0.25");
    }

    // 3. Descripción — opacidad final 0.75 (igual que el SCSS)
    if (desc) {
      tl.to(desc, { opacity: 0.75, y: 0, duration: 0.5, ease: "power3.out" }, "-=0.2");
    }

    // 4. Items de lista en stagger — clearProps al final para que CSS tome el control
    if (listItems.length) {
      tl.to(listItems, {
        opacity: 0.55,
        y: 0,
        duration: 0.4,
        stagger: 0.07,
        ease: "power3.out",
        onComplete: () => {
          listItems.forEach((li) => gsap.set(li, { clearProps: "opacity,y,transform" }));
        },
      }, "-=0.1");
    }
  });
}

export function initScrollMarquee(scope = document) {
  const wrap = scope.querySelector(".marquee-scroll");
  const text = scope.querySelector(".marquee-scroll__text");
  if (!wrap || !text) return;

  const build = () => {
    // cuánto “sobra” para poder desplazar (texto - contenedor)
    const overflow = Math.max(0, text.scrollWidth - wrap.clientWidth);

    gsap.set(text, { x: 0 });

    ScrollTrigger.create({
      trigger: wrap,
      start: "top 80%",
      end: "bottom 20%",
      // markers: true,
      scrub: true,
      onUpdate: (self) => {
        // self.progress: 0 → 1
        gsap.to(text, {
          x: -overflow * self.progress,
          duration: 0,
          overwrite: true,
        });
      },
      invalidateOnRefresh: true,
      // markers: true,
    });
  };

  build();
  window.addEventListener("resize", () => ScrollTrigger.refresh());
}

export function initBlockServices() {
  const section = document.querySelector(".block-services");
  if (!section) return;

  gsap.registerPlugin(ScrollTrigger);

  const navInner = section.querySelector(".block-services__nav");
  const items = section.querySelectorAll(".block-services__item");
  const listItems = section.querySelectorAll(".block-services__list-item");

  const mm = gsap.matchMedia();

  mm.add("(min-width: 992px)", () => {
    // Pin de la navegación
    ScrollTrigger.create({
      trigger: section,
      start: "top top",
      end: () => `bottom bottom`,
      pin: navInner,
      pinSpacing: false,
    });

    // Animación integrada con scroll para cada servicio
    items.forEach((item, index) => {
      const title = item.querySelector(".block-services__title");
      const desc = item.querySelector(".block-services__desc");
      const image = item.querySelector(".block-services__image");

      const elementsToAnimate = [title, desc, image].filter(Boolean);

      // Timeline con scrub - animación ligada al scroll
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: item,
          start: "top 85%",
          end: "top 35%",
          scrub: 0.5, // Suavidad de la animación (0 = instantáneo, 1+ = más suave)
        },
      });

      tl.fromTo(
        elementsToAnimate,
        {
          opacity: 0,
          y: 50,
        },
        {
          opacity: 1,
          y: 0,
          stagger: 0.05,
          ease: "none",
        },
      );

      // Actualizar item activo en la navegación
      ScrollTrigger.create({
        trigger: item,
        start: "top center",
        end: "bottom center",
        onEnter: () => setActiveItem(index),
        onEnterBack: () => setActiveItem(index),
      });
    });

    // Click en items de navegación
    listItems.forEach((listItem) => {
      listItem.addEventListener("click", handleNavClick);
    });

    function handleNavClick(e) {
      const index = parseInt(e.currentTarget.dataset.index);
      const targetItem = items[index];
      if (!targetItem) return;

      const y = targetItem.getBoundingClientRect().top + window.scrollY - 100;

      window.scrollTo({
        top: y,
        behavior: "smooth",
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach((st) => st.kill());
      listItems.forEach((listItem) => {
        listItem.removeEventListener("click", handleNavClick);
      });
    };
  });

  // Mobile
  mm.add("(max-width: 991px)", () => {
    items.forEach((item) => {
      const title = item.querySelector(".block-services__title");
      const desc = item.querySelector(".block-services__desc");
      const image = item.querySelector(".block-services__image");

      const elementsToAnimate = [title, desc, image].filter(Boolean);

      gsap
        .timeline({
          scrollTrigger: {
            trigger: item,
            start: "top 85%",
            end: "top 50%",
            scrub: 0.3,
          },
        })
        .fromTo(
          elementsToAnimate,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, stagger: 0.03, ease: "none" },
        );
    });

    return () => {
      ScrollTrigger.getAll().forEach((st) => st.kill());
    };
  });

  function setActiveItem(index) {
    listItems.forEach((item, i) => {
      item.classList.toggle("is-active", i === index);
    });
  }
}

// ============================================
// 1. CLIP REVEAL - Máscara horizontal elegante
// Uso: data-text-clip
// ============================================
export function initTextClip(scope = document) {
  const els = Array.from(scope.querySelectorAll("[data-text-clip]"));
  if (!els.length) return () => {};

  const instances = [];

  els.forEach((el) => {
    if (el.dataset.processed === "true") return;
    el.dataset.processed = "true";

    const split = new SplitText(el, { type: "lines", linesClass: "clip-line" });

    split.lines.forEach((line) => {
      const wrapper = document.createElement("div");
      wrapper.className = "clip-mask";
      line.parentNode.insertBefore(wrapper, line);
      wrapper.appendChild(line);
    });

    gsap.set(split.lines, { yPercent: 140 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: "top 80%",
        toggleActions: "play none none reverse",
      },
    });

    tl.to(split.lines, {
      yPercent: 0,
      duration: 1,
      ease: "power4.out",
      stagger: 0.12,
    });

    instances.push({ split, tl, el });
  });

  return () =>
    instances.forEach(({ split, tl, el }) => {
      tl?.kill();
      split.revert();
      delete el.dataset.processed;
    });
}

// ============================================
// 2. CHARS ROTATE 3D - Caracteres con rotación
// Uso: data-text-rotate
// ============================================
export function initTextRotate(scope = document) {
  const els = Array.from(scope.querySelectorAll("[data-text-rotate]"));
  if (!els.length) return () => {};

  const instances = [];

  els.forEach((el) => {
    if (el.dataset.processed === "true") return;
    el.dataset.processed = "true";

    const split = new SplitText(el, {
      type: "chars,words",
      charsClass: "rotate-char",
    });

    gsap.set(split.chars, {
      opacity: 0,
      rotateX: -90,
      yPercent: 50,
      transformOrigin: "50% 50% -30px",
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: "top 80%",
        toggleActions: "play none none reverse",
      },
    });

    tl.to(split.chars, {
      opacity: 1,
      rotateX: 0,
      yPercent: 0,
      duration: 0.8,
      ease: "power3.out",
      stagger: 0.025,
    });

    instances.push({ split, tl, el });
  });

  return () =>
    instances.forEach(({ split, tl, el }) => {
      tl?.kill();
      split.revert();
      delete el.dataset.processed;
    });
}

// ============================================
// 3. SCRAMBLE TEXT - Efecto hacker/glitch
// Uso: data-text-scramble
// ============================================
export function initTextScramble(scope = document) {
  const els = Array.from(scope.querySelectorAll("[data-text-scramble]"));
  if (!els.length) return () => {};

  const chars = "!<>-_\\/[]{}—=+*^?#_____";
  const instances = [];

  els.forEach((el) => {
    if (el.dataset.processed === "true") return;
    el.dataset.processed = "true";

    const originalText = el.textContent;

    gsap.set(el, { opacity: 0 });

    ScrollTrigger.create({
      trigger: el,
      start: "top 80%",
      onEnter: () => scramble(el, originalText),
      onLeaveBack: () => {
        el.textContent = originalText;
        gsap.set(el, { opacity: 0 });
      },
    });

    instances.push({ el, originalText });
  });

  function scramble(el, finalText) {
    let iteration = 0;
    const speed = 30;
    const totalIterations = finalText.length * 2;

    gsap.to(el, { opacity: 1, duration: 0.1 });

    const interval = setInterval(() => {
      el.textContent = finalText
        .split("")
        .map((char, i) => {
          if (char === " ") return " ";
          if (i < iteration / 2) return finalText[i];
          return chars[Math.floor(Math.random() * chars.length)];
        })
        .join("");

      iteration++;
      if (iteration > totalIterations) clearInterval(interval);
    }, speed);
  }

  return () =>
    instances.forEach(({ el, originalText }) => {
      el.textContent = originalText;
      delete el.dataset.processed;
    });
}

// ============================================
// 4. WAVE - Onda que recorre el texto
// Uso: data-text-wave
// ============================================
export function initTextWave(scope = document) {
  const els = Array.from(scope.querySelectorAll("[data-text-wave]"));
  if (!els.length) return () => {};

  const instances = [];

  els.forEach((el) => {
    if (el.dataset.processed === "true") return;
    el.dataset.processed = "true";

    const split = new SplitText(el, { type: "chars", charsClass: "wave-char" });

    gsap.set(split.chars, { opacity: 0, yPercent: 100 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: "top 80%",
        toggleActions: "play none none reverse",
      },
    });

    tl.to(split.chars, {
      opacity: 1,
      yPercent: 0,
      duration: 0.6,
      ease: "back.out(1.7)",
      stagger: {
        each: 0.03,
        from: "start",
      },
    });

    instances.push({ split, tl, el });
  });

  return () =>
    instances.forEach(({ split, tl, el }) => {
      tl?.kill();
      split.revert();
      delete el.dataset.processed;
    });
}

// ============================================
// 5. FADE BLUR IN - Fade con blur sutil
// Uso: data-text-blur
// ============================================
export function initTextBlur(scope = document) {
  const els = Array.from(scope.querySelectorAll("[data-text-blur]"));
  if (!els.length) return () => {};

  const instances = [];

  els.forEach((el) => {
    if (el.dataset.processed === "true") return;
    el.dataset.processed = "true";

    const split = new SplitText(el, { type: "words", wordsClass: "blur-word" });

    gsap.set(split.words, {
      opacity: 0,
      filter: "blur(10px)",
      yPercent: 20,
    });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el,
        start: "top 80%",
        end: "top 30%",
        scrub: 0.8,
      },
    });

    tl.to(split.words, {
      opacity: 1,
      filter: "blur(0px)",
      yPercent: 0,
      stagger: 0.05,
    });

    instances.push({ split, tl, el });
  });

  return () =>
    instances.forEach(({ split, tl, el }) => {
      tl?.scrollTrigger?.kill();
      tl?.kill();
      split.revert();
      delete el.dataset.processed;
    });
}

// ============================================
// 6. PARALLAX LINES - Líneas a diferentes velocidades
// Uso: data-text-parallax
// ============================================
export function initTextParallax(scope = document) {
  const els = Array.from(scope.querySelectorAll("[data-text-parallax]"));
  if (!els.length) return () => {};

  const instances = [];

  els.forEach((el) => {
    if (el.dataset.processed === "true") return;
    el.dataset.processed = "true";

    const split = new SplitText(el, {
      type: "lines",
      linesClass: "parallax-line",
    });

    split.lines.forEach((line, i) => {
      const wrapper = document.createElement("div");
      wrapper.className = "parallax-mask";
      line.parentNode.insertBefore(wrapper, line);
      wrapper.appendChild(line);

      // Velocidad diferente para líneas pares/impares
      const xStart = i % 2 === 0 ? -30 : 30;

      gsap.set(line, { opacity: 0, x: xStart });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: wrapper,
            start: "top 85%",
            end: "top 40%",
            scrub: 0.5,
          },
        })
        .to(line, { opacity: 1, x: 0 });
    });

    instances.push({ split, el });
  });

  return () =>
    instances.forEach(({ split, el }) => {
      split.revert();
      delete el.dataset.processed;
    });
}

// ============================================
// 7. TYPEWRITER - Efecto máquina de escribir
// Uso: data-text-typewriter
// ============================================
export function initTextTypewriter(scope = document) {
  const els = Array.from(scope.querySelectorAll("[data-text-typewriter]"));
  if (!els.length) return () => {};

  const instances = [];

  els.forEach((el) => {
    if (el.dataset.processed === "true") return;
    el.dataset.processed = "true";

    const text = el.textContent;
    el.textContent = "";
    el.style.borderRight = "2px solid currentColor";

    let hasPlayed = false;

    ScrollTrigger.create({
      trigger: el,
      start: "top 80%",
      onEnter: () => {
        if (hasPlayed) return;
        hasPlayed = true;
        typewrite(el, text);
      },
    });

    instances.push({ el, text });
  });

  function typewrite(el, text) {
    let i = 0;
    const speed = 50;

    // Cursor blink
    gsap.to(el, {
      borderColor: "transparent",
      repeat: -1,
      yoyo: true,
      duration: 0.5,
      ease: "steps(1)",
    });

    const interval = setInterval(() => {
      el.textContent = text.slice(0, i + 1);
      i++;
      if (i >= text.length) {
        clearInterval(interval);
        setTimeout(() => {
          el.style.borderRight = "none";
        }, 1500);
      }
    }, speed);
  }

  return () =>
    instances.forEach(({ el, text }) => {
      el.textContent = text;
      el.style.borderRight = "none";
      delete el.dataset.processed;
    });
}

export function initCardBio(scope = document) {
  const rows = document.querySelectorAll("[data-member-trigger]");

  rows.forEach((row, index) => {
    const card = row.querySelector(".member-card");
    if (!card) return;

    card.setAttribute("data-card-index", index);
    row.setAttribute("data-row-index", index);
    document.body.appendChild(card);

    row.addEventListener("mouseenter", (e) => {
      document
        .querySelectorAll(".member-card")
        .forEach((c) => c.classList.remove("is-visible"));

      document.body.style.cursor = "none";
      row.style.cursor = "none";

      card.classList.add("is-visible");

      // Forzar reflow ANTES de calcular posición
      card.offsetHeight;

      // Usar requestAnimationFrame para asegurar que el layout está listo
      requestAnimationFrame(() => {
        updateCardPosition(e, card);
      });
    });

    row.addEventListener("mousemove", (e) => {
      if (card.classList.contains("is-visible")) {
        updateCardPosition(e, card);
      }
    });

    row.addEventListener("mouseleave", () => {
      card.classList.remove("is-visible");
      document.body.style.cursor = "";
      row.style.cursor = "";
    });
  });

  function updateCardPosition(e, card) {
    // Forzar que el navegador calcule las dimensiones reales
    const cardWidth = card.offsetWidth || 320;
    const cardHeight = card.offsetHeight || 200;

    let x = e.clientX - cardWidth / 2;
    let y = e.clientY - cardHeight / 2;

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    x = Math.max(10, Math.min(x, vw - cardWidth - 10));
    y = Math.max(10, Math.min(y, vh - cardHeight - 10));

    card.style.left = `${x}px`;
    card.style.top = `${y}px`;
  }
}

export function initGalleryScroll() {
  const wrapper = document.querySelector('[data-gallery="wrapper"]');
  const viewport = document.querySelector('[data-gallery="viewport"]');
  const track = document.querySelector('[data-gallery="track"]');
  const items = document.querySelectorAll('[data-gallery="item"]');

  if (!wrapper || !viewport || !track || items.length === 0) return;

  const isMobile = window.innerWidth <= 768;
  const startWidth = isMobile ? 50 : 30;
  const endWidth = isMobile ? 90 : 95;
  const endHeight = isMobile ? 90 : 95;
  const totalSlides = items.length;

  // Usar CSS en vez de JS para el ancho de items
  // Los items ocupan 100% del viewport padre
  const setItemWidths = () => {
    const vpWidth = gsap.getProperty(viewport, "width"); // Usa el valor real de GSAP
    items.forEach((item) => {
      gsap.set(item, { width: vpWidth });
    });
    // Actualizar también el track width total
    gsap.set(track, { width: vpWidth * totalSlides });
  };

  // Calcular distancia basada en el ancho FINAL del viewport
  const getEndViewportWidth = () => {
    return (window.innerWidth * endWidth) / 100;
  };

  const getTrackDistance = () => {
    return getEndViewportWidth() * (totalSlides - 1);
  };

  // Inicializar
  setItemWidths();

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: wrapper,
      start: "top top",
      end: () => `+=${window.innerHeight * (totalSlides + 1)}`,
      pin: true,
      pinSpacing: true,
      scrub: 1,
      invalidateOnRefresh: true,
      refreshPriority: -10, // 👈 Prioridad baja = se calcula primero
    },
  });

  // 1. El VIEWPORT crece - también escalar los items
  tl.to(
    viewport,
    {
      width: `${endWidth}vw`,
      height: `${endHeight}vh`,
      duration: 0.2,
      ease: "power2.inOut",
    },
    0,
  );

  // Los items crecen al mismo tiempo que el viewport
  tl.to(
    items,
    {
      width: () => getEndViewportWidth(),
      duration: 0.2,
      ease: "power2.inOut",
    },
    0,
  );

  // Track width también crece
  tl.to(
    track,
    {
      width: () => getEndViewportWidth() * totalSlides,
      duration: 0.2,
      ease: "power2.inOut",
    },
    0,
  );

  // 2. Las imágenes pasan DENTRO del viewport
  tl.to(track, {
    x: () => -getTrackDistance(),
    duration: 1,
    ease: "none",
  });

  // Refresh en resize
  let timeout;
  window.addEventListener("resize", () => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 250);
  });
}

export function initBlockClubsAgreements() {
  const section = document.querySelector(".block-clubs-agreements");
  if (!section) return;

  const stickyCol = section.querySelector(".block-clubs-agreements__sticky-col");
  const items = section.querySelectorAll(".block-clubs-agreements__item");
  const listItems = section.querySelectorAll(
    ".block-clubs-agreements__list-item",
  );

  const mm = gsap.matchMedia();

  mm.add("(min-width: 992px)", () => {
    ScrollTrigger.create({
      trigger: section,
      start: "top top",
      end: () => `bottom bottom`,
      pin: stickyCol,
      pinSpacing: false,
    });

    items.forEach((item, index) => {
      const title = item.querySelector(".block-clubs-agreements__item-title");
      const desc = item.querySelector(".block-clubs-agreements__desc");
      const list = item.querySelector(".block-clubs-agreements__bullet-list");
      const elementsToAnimate = [title, desc, list].filter(Boolean);

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: item,
          start: "top 85%",
          end: "top 35%",
          scrub: 0.5,
        },
      });

      tl.fromTo(
        elementsToAnimate,
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, stagger: 0.05, ease: "none" },
      );

      ScrollTrigger.create({
        trigger: item,
        start: "top center",
        end: "bottom center",
        onEnter: () => setActiveItem(index),
        onEnterBack: () => setActiveItem(index),
      });
    });

    listItems.forEach((listItem) => {
      listItem.addEventListener("click", handleNavClick);
    });

    function handleNavClick(e) {
      const index = parseInt(e.currentTarget.dataset.index);
      const targetItem = items[index];
      if (!targetItem) return;
      const y = targetItem.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }

    return () => {
      ScrollTrigger.getAll().forEach((st) => st.kill());
      listItems.forEach((listItem) => {
        listItem.removeEventListener("click", handleNavClick);
      });
    };
  });

  mm.add("(max-width: 991px)", () => {
    items.forEach((item) => {
      const elementsToAnimate = Array.from(
        item.querySelectorAll(
          ".block-clubs-agreements__item-title, .block-clubs-agreements__desc, .block-clubs-agreements__bullet-list",
        ),
      ).filter(Boolean);

      gsap
        .timeline({
          scrollTrigger: {
            trigger: item,
            start: "top 85%",
            end: "top 50%",
            scrub: 0.3,
          },
        })
        .fromTo(
          elementsToAnimate,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, stagger: 0.03, ease: "none" },
        );
    });

    return () => {
      ScrollTrigger.getAll().forEach((st) => st.kill());
    };
  });

  function setActiveItem(index) {
    listItems.forEach((item, i) => {
      item.classList.toggle("is-active", i === index);
    });
  }
}

export function initImageContact() {
  if (window.innerWidth > 767) return;

  const el = document.querySelector("[data-tilt-mobile]");
  const hint = document.querySelector(".gyro-hint");
  if (!el) return;

  let gyroEnabled = false;

  function handleOrientation(e) {
    const rotateX = Math.min(Math.max(e.beta - 45, -15), 15);
    const rotateY = Math.min(Math.max(e.gamma, -15), 15);
    el.style.transform = `perspective(500px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  }

  function enableGyro() {
    if (gyroEnabled) return;

    // iOS 13+ necesita permiso
    if (typeof DeviceOrientationEvent.requestPermission === "function") {
      DeviceOrientationEvent.requestPermission()
        .then((response) => {
          if (response === "granted") {
            window.addEventListener("deviceorientation", handleOrientation);
            gyroEnabled = true;
            hint.classList.add("hidden");
          }
        })
        .catch(console.error);
    } else {
      // Android y otros
      window.addEventListener("deviceorientation", handleOrientation);
      gyroEnabled = true;
      hint.classList.add("hidden");
    }
  }

  el.addEventListener("click", enableGyro);
  el.addEventListener("touchend", enableGyro);
}
